// File: frontend/src/services/apiService.js

import { API_CONFIG } from '../config/api';
import { API_ENDPOINTS, STORAGE_KEYS } from '../config/constants';

class ApiService {
  constructor() {
    this.baseUrl = API_CONFIG.baseUrl;
    this.enterpriseId = API_CONFIG.enterpriseId;
  }

  async executeCommand(command, data = {}, userId = null, requiresAuth = false) {
    const endpoint = requiresAuth ? API_ENDPOINTS.PRIVATE_ACTIONS : API_ENDPOINTS.FRONTEND_ACTIONS;
    
    const requestBody = {
      command,
      data: {
        ...data,
        user_id: data.user_id || userId,
        requesting_user_id: userId,
        enterprise_id: this.enterpriseId
      }
    };

    const headers = {
      'Content-Type': 'application/json',
    };

    if (requiresAuth) {
      const tokensStr = localStorage.getItem(STORAGE_KEYS.AUTH_TOKENS);
      
      if (!tokensStr) {
        throw new Error('Not authenticated - no tokens found');
      }
      
      let tokens;
      try {
        tokens = JSON.parse(tokensStr);
      } catch (e) {
        throw new Error('Not authenticated - invalid token format');
      }
      
      if (!tokens.id_token) {
        throw new Error('Not authenticated - no id_token');
      }
      
      headers['Authorization'] = `Bearer ${tokens.id_token}`;
    }

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          const error = new Error(result.error || 'Authentication failed');
          error.code = result.code || 'AUTH_FAILED';
          error.status = response.status;
          throw error;
        }
        
        if (response.status === 400 && result.code) {
          return result;
        }
        
        const error = new Error(result.error || result.message || `HTTP ${response.status}`);
        error.code = result.code;
        error.details = result.details;
        error.status = response.status;
        throw error;
      }

      return result;
      
    } catch (error) {
      console.error('API Error:', {
        command,
        message: error.message,
        code: error.code,
        status: error.status
      });
      throw error;
    }
  }

  async signUp(data) {
    return this.executeCommand('signUp', data, null, false);
  }

  async verifyPhoneOTP(data) {
    return this.executeCommand('verifyPhoneOTP', data, null, false);
  }

  async verifyEmailToken(data) {
    return this.executeCommand('verifyEmailToken', data, null, false);
  }

  async signIn(data) {
    return this.executeCommand('signIn', data, null, false);
  }

  async verifySignInOTP(data) {
    return this.executeCommand('verifySignInOTP', data, null, false);
  }

  async resendOTP(verificationId, correlationId = null) {
    const data = {
      verification_id: verificationId
    };
    
    if (correlationId) {
      data.correlation_id = correlationId;
    }
    
    return this.executeCommand('resendOTP', data, null, false);
  }

  async resendVerificationEmail(userId, correlationId = null) {
    const data = {
      user_id: userId
    };
    
    if (correlationId) {
      data.correlation_id = correlationId;
    }
    
    return this.executeCommand('resendVerificationEmail', data, null, false);
  }

  async getProfile(userId) {
    return this.executeCommand('getProfile', {}, userId, true);
  }

  async updateProfile(data, userId) {
    return this.executeCommand('updateProfile', data, userId, true);
  }

  async changePassword(data, userId) {
    return this.executeCommand('changePassword', data, userId, true);
  }

  async signOut(userId) {
    return this.executeCommand('signOut', {
      reason: 'user_initiated'
    }, userId, true);
  }

  async refreshTokens(refreshToken, enterpriseId, userId) {
    return this.executeCommand('refreshTokens', {
      refresh_token: refreshToken,
      enterprise_id: enterpriseId
    }, userId, false);
  }
  
  async getChatHistory(chatId, userId) {
    return this.executeCommand('getChatHistory', { chat_id: chatId }, userId, true);
  }

  async bootstrapAnonymousUser(data) {
    return this.executeCommand('bootstrapAnonymousUser', data, null, false);
  }

  async trackUserAction(data) {
    return this.executeCommand('trackUserAction', data, null, false);
  }

  async updateConsentPreferences(data) {
    return this.executeCommand('updateConsentPreferences', data, null, false);
  }

  async submitContactForm(data) {
    return this.executeCommand('submitContactForm', data, null, false);
  }

  async subscribeNewsletter(data) {
    return this.executeCommand('subscribeNewsletter', data, null, false);
  }
  
  async unsubscribeNewsletter(data) {
    return this.executeCommand('unsubscribeNewsletter', data, null, false);
  }

  async resubscribeNewsletter(data) {
    return this.executeCommand('resubscribeNewsletter', data);
  }

  async getNewsletterPreferences(data) {
    return this.executeCommand('getNewsletterPreferences', data);
  }
  
  async updateNewsletterPreferences(data) {
    return this.executeCommand('updateNewsletterPreferences', data);
  }

  // ✅ NEW: Upload & Processing Methods
  async uploadFile(data, userId) {
    return this.executeCommand('uploadFile', data, userId, true);
  }

  async processDocument(data, userId) {
    return this.executeCommand('processDocument', data, userId, true);
  }

  async getFileMetadata(data, userId) {
    return this.executeCommand('getFileMetadata', data, userId, true);
  }

  async getUserFiles(data, userId) {
    return this.executeCommand('getUserFiles', data, userId, true);
  }

  async getUserChats(data, userId) {
    return this.executeCommand('getUserChats', data, userId, true);
  }

  async getUserFile(data, userId) {
    return this.executeCommand('getUserFile', data, userId, true);
  }
  
  async deleteFile(data, userId) {
    return this.executeCommand('deleteFile', data, userId, true);
  }

  async startChat(data, userId) {
    return this.executeCommand('startChat', {
      user_id: userId,
      ...data
    }, userId, true);
  }

  // ========== UMS (User Management System) Methods ==========
  
  async getUsers(data, userId) {
    return this.executeCommand('getUsers', data, userId, true);
  }

  async getUserStats(data, userId) {
    return this.executeCommand('getUserStats', data, userId, true);
  }

  async getUserDetails(data, userId) {
    return this.executeCommand('getUserDetails', data, userId, true);
  }

  // ========== EMS (Event Management System) Methods ==========
  async getEvents(data, userId) {
    return this.executeCommand('getEvents', data, userId, true);
  }
  
  async getEventStats(data, userId) {
    return this.executeCommand('getEventStats', data, userId, true);
  }
  
  async getEventDetails(data, userId) {
    return this.executeCommand('getEventDetails', data, userId, true);
  }

  async getUserEvents(data, userId) {
    return this.executeCommand('getUserEvents', data, userId, true);
  }

  // ========== AI (Agent Intelligence) Methods ==========

  async getAgents(data, userId) {
    return this.executeCommand('getAgents', data, userId, true);
  }
  
  async getAgentStats(data, userId) {
    return this.executeCommand('getAgentStats', data, userId, true);
  }
  
  async getAgentDetails(data, userId) {
    return this.executeCommand('getAgentDetails', data, userId, true);
  }

}

const apiServiceInstance = new ApiService();
export default apiServiceInstance;